import {
  Color,
  Mesh,
  MeshBasicMaterial,
  ShaderMaterial,
  Vector3,
} from '@technology/web3d';
import { StationInfo } from '@trimble/web3d-providers';
import { createStationMarkerMesh } from './station-marker-mesh';
import { createStationPathMesh } from './station-path-mesh';
import { calculateDistanceBasedScale } from '../distance-based-scale-calculation';

interface StationMarkerUniforms {
  innerOpacity: { value: number };
  outerOpacity: { value: number };
  globalOpacity: { value: number };
  arrowAngle: { value: number };
  hasArrow: { value: number };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Required for ShaderMaterial compatibility
  [uniform: string]: { value: any };
}

interface StationMarkerMaterial extends ShaderMaterial {
  uniforms: StationMarkerUniforms;
}

/**
 * Lightweight metadata for all stations (no GPU resources).
 * Used for LOD distance checks, path color lookups, and position data.
 * Active markers are promoted to StationPoint when within LOD threshold.
 * Keyed by `key` (position string) for O(1) lookup without reindexing.
 */
export interface StationData {
  key: string;
  stationIndex: number;
  originalPosition: Vector3;
  position: Vector3;
  distance: number;
  isWithinMarkerDistance: boolean;
  directionAngle: number;
  hasArrow: boolean;
}

export interface StationPoint {
  stationIndex: number;
  originalPosition: Vector3;
  position: Vector3;
  distance: number;
  mesh: Mesh;
  targetOpacity: number;
  baseScale: number;
  targetScale: number;
  isWithinMarkerDistance: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Storing original raycast function
  originalRaycast?: any;
}

export interface PathSegment {
  mesh: Mesh;
  midpoint: Vector3;
  distance: number;
  baseWidth: number;
  startStationIndex: number;
  endStationIndex: number;
  currentColor: Color;
  targetColor: Color;
}

export interface StationVisualConfig {
  stationPlaneSize: number;
  stationPointSize: number;
  pathWidthRatio: number;
  stationHeightOffset: number;
  stationNearBoundary: number;
  stationFarBoundary: number;
  stationRenderOrder: number;
  pathRenderOrder: number;
  pathHeightOffset: number;
}

/**
 * Creates a station marker point at the specified position
 * @param directionAngle - Angle in radians for arrow direction (0 = north/up). Pass 0 if no arrow needed.
 * @param hasArrow - Whether to display the directional arrow
 */
export function createStationPointVisual(
  stationPosition: Vector3,
  index: number,
  initialOpacity: number,
  stationZ: number,
  cameraPosition: Vector3,
  config: StationVisualConfig,
  directionAngle: number = 0,
  hasArrow: boolean = false
): StationPoint {
  const position = new Vector3(stationPosition.x, stationPosition.y, stationZ);

  const mesh = createStationMarkerMesh({
    planeGeometrySize: config.stationPlaneSize,
    directionAngle,
    hasArrow,
    depthTest: false,
  });

  mesh.position.copy(position);
  mesh.renderOrder = config.stationRenderOrder;

  // No near-zone scaling — see StationMarkerPipeline.updateScale.
  const initialScale = calculateDistanceBasedScale(
    cameraPosition,
    position,
    config.stationPointSize,
    undefined,
    config.stationFarBoundary
  );

  mesh.scale.setScalar(initialScale);
  mesh.updateMatrixWorld(true);
  mesh.geometry.computeBoundingBox();

  const material = mesh.material as StationMarkerMaterial;
  material.uniforms.globalOpacity.value = initialOpacity;

  return {
    stationIndex: index,
    originalPosition: stationPosition.clone(),
    position,
    distance: 0,
    mesh,
    targetOpacity: initialOpacity,
    baseScale: initialScale,
    targetScale: initialScale,
    isWithinMarkerDistance: false, // Initially false so first LOD check triggers transition
    originalRaycast: mesh.raycast, // Store original raycast function
  };
}

/**
 * Creates path meshes between stations and returns segments
 */
export function createPathSegmentsVisuals(
  stationsInfo: StationInfo[],
  getStationZ: (position: Vector3) => number,
  pathOpacity: number,
  config: StationVisualConfig,
  initialPathColor: Color
): { segments: PathSegment[]; sharedMaterial: MeshBasicMaterial | null } {
  const pathWidth = config.stationPlaneSize * config.pathWidthRatio;

  const transformedStations = stationsInfo.map((station) => ({
    position: new Vector3(
      station.position.x,
      station.position.y,
      getStationZ(station.position)
    ),
    runId: station.runId,
  }));

  const meshes = createStationPathMesh({
    stationsInfo: transformedStations,
    pathWidth,
    pathOpacity,
    pathHeightOffset: config.pathHeightOffset,
    renderOrder: config.pathRenderOrder,
  });

  if (!meshes || meshes.length === 0) {
    return { segments: [], sharedMaterial: null };
  }

  // Track current segment index (accounting for skipped segments due to runId mismatches)
  let stationIndexForSegment = 0;

  const segments: PathSegment[] = meshes.map((mesh) => {
    const geometry = mesh.geometry;
    geometry.computeBoundingBox();
    const boundingBox = geometry.boundingBox;

    if (!boundingBox) {
      throw new Error('Bounding box not computed for path mesh');
    }

    const midpoint = new Vector3();
    boundingBox.getCenter(midpoint);
    mesh.localToWorld(midpoint);

    // Find which stations this segment connects
    // We need to account for skipped segments (different runIds)
    let startIndex = stationIndexForSegment;
    let endIndex = stationIndexForSegment + 1;

    // Advance stationIndexForSegment  to find next valid pair
    while (
      startIndex < stationsInfo.length - 1 &&
      stationsInfo[startIndex].runId !== stationsInfo[startIndex + 1].runId
    ) {
      startIndex++;
    }
    endIndex = startIndex + 1;
    stationIndexForSegment = endIndex;

    // Initialize colors (start with initial color)
    // Each mesh already has its own material from createStationPathMesh
    const currentColor = initialPathColor.clone();
    const targetColor = initialPathColor.clone();

    return {
      mesh,
      midpoint,
      distance: 0,
      baseWidth: pathWidth,
      startStationIndex: startIndex,
      endStationIndex: endIndex,
      currentColor,
      targetColor,
    };
  });

  return { segments, sharedMaterial: null };
}
